import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import MatchRow from "@/components/feature/MatchRow";
import { leagues } from "@/mocks/leagues";
import { matches } from "@/mocks/matches";
import { formatDateCN, formatWeekday, relativeDayLabel } from "@/utils/format";

export default function SchedulePage() {
  const [date, setDate] = useState<string>("all");
  const [league, setLeague] = useState<string>("all");

  const dates = useMemo(() => {
    const set = Array.from(new Set(matches.map((m) => m.date)));
    return set.sort((a, b) => a.localeCompare(b));
  }, []);

  const filtered = useMemo(
    () =>
      matches
        .filter((m) => (date === "all" ? true : m.date === date))
        .filter((m) => (league === "all" ? true : m.leagueSlug === league))
        .sort((a, b) => a.startTime.localeCompare(b.startTime)),
    [date, league]
  );

  const grouped = useMemo(() => {
    const map = new Map<string, typeof filtered>();
    filtered.forEach((m) => {
      const arr = map.get(m.date) || [];
      arr.push(m);
      map.set(m.date, arr);
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  return (
    <div className="w-full">
      <section className="w-full bg-primary-950 px-4 md:px-6 py-10 md:py-14">
        <nav className="flex items-center gap-1.5 text-xs text-primary-200 mb-3">
          <Link to="/" className="hover:text-accent-400 cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-accent-400">赛程</span>
        </nav>
        <h1 className="font-heading font-semibold text-3xl md:text-4xl text-background-50">
          篮球赛程安排
        </h1>
        <p className="mt-3 max-w-2xl text-primary-100 text-sm md:text-base leading-relaxed">
          查看未来与往期全部赛事安排，按日期与联赛快速定位你关心的比赛。所有时间均为北京时间，数据每日更新。
        </p>
      </section>

      <section className="w-full px-4 md:px-6 py-8 md:py-10">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
            <div className="flex-1 min-w-0">
              <p className="text-xs text-foreground-500 mb-2">选择日期</p>
              <div className="flex items-center gap-2 overflow-x-auto pb-1">
                <button
                  type="button"
                  onClick={() => setDate("all")}
                  className={`shrink-0 px-3.5 py-2 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    date === "all"
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  全部
                </button>
                {dates.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setDate(d)}
                    className={`shrink-0 flex flex-col items-center px-3.5 py-2 rounded-md text-xs transition-colors cursor-pointer whitespace-nowrap ${
                      date === d
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-700 hover:bg-background-200"
                    }`}
                  >
                    <span className="font-label font-semibold">
                      {relativeDayLabel(d) || formatWeekday(d)}
                    </span>
                    <span className={date === d ? "text-background-50/80" : "text-foreground-500"}>
                      {formatDateCN(d)}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="lg:w-56 shrink-0">
              <p className="text-xs text-foreground-500 mb-2">赛事联赛</p>
              <select
                value={league}
                onChange={(e) => setLeague(e.target.value)}
                className="w-full bg-background-100 border border-background-200 rounded-md px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 cursor-pointer"
              >
                <option value="all">全部联赛</option>
                {leagues.map((l) => (
                  <option key={l.slug} value={l.slug}>
                    {l.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {grouped.length === 0 ? (
          <div className="rounded-lg border border-background-200 bg-background-50 py-16 text-center">
            <i className="ri-calendar-close-line text-4xl text-foreground-400" />
            <p className="mt-3 text-foreground-700">该条件下暂无赛程安排</p>
            <p className="mt-1 text-sm text-foreground-500">试试切换日期或选择其他联赛</p>
            <button
              type="button"
              onClick={() => {
                setDate("all");
                setLeague("all");
              }}
              className="mt-5 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              重置筛选
            </button>
          </div>
        ) : (
          <div className="space-y-8">
            {grouped.map(([day, items]) => (
              <div key={day}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-accent-500 text-background-50 dark:text-foreground-950 text-xs font-label font-semibold">
                    {relativeDayLabel(day) || "赛程日"}
                  </span>
                  <h2 className="font-heading text-lg md:text-xl text-foreground-950">
                    {formatDateCN(day)} {formatWeekday(day)}
                  </h2>
                  <span className="text-xs text-foreground-500">共 {items.length} 场</span>
                </div>
                <div className="space-y-3">
                  {items.map((m) => (
                    <MatchRow key={m.id} match={m} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}